import React, { useEffect } from 'react';
import {
  Button,
  Grid,
  OutlinedInput,
  InputLabel,
  Stack,
  FormHelperText,
  InputAdornment,
  Box,
  Autocomplete,
  Typography,
  TextField,
  Select,
  MenuItem,
  Chip,
  Checkbox
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { COMPANY_URL_SUFFIX, fiscalYearValue } from 'constant/AppConfig';

const autocompleteStyle = {
  '.MuiOutlinedInput-root': {
    padding: '3px'
  },
  '.MuiAutocomplete-popupIndicator': {
    height: '30px'
  },
  '.MuiAutocomplete-clearIndicator': {
    height: '30px'
  }
};

const CompanyInfo = ({ handleNext, companyInfo, setCompanyInfo, groupCompanies }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const validationSchema = Yup.object().shape({
    company_name: Yup.string().required('Company Name is required.'),
    web_url: Yup.string().url('Invalid URL').required('Company Website is required.'),
    unique_url_component: Yup.string().required('Company Unique URL is required.').matches(/^\S*$/, 'Space is not allowed.'),
    support_email: Yup.string().email('Invalid email.'),
    fiscal_start_month: Yup.number().required('Company fiscal year period is required.'),
    // subscription_plans: Yup.array(),
    // update_frequency: Yup.string().required('Update frequency is required.'),
    // user_limit: Yup.number().required('Number of users is required.').min(1, 'Number of users must be at least 1.'),
    // minimum 2 group company
    group_of_companies: Yup.array().required('Group companies are required.').min(2, 'At least two group companies are required.')
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      company_name: '',
      web_url: 'https://',
      unique_url_component: '',
      support_email: '',
      fiscal_start_month: null,
      group_of_companies: [],
      // subscription_plans: [],
      // update_frequency: '',
      is_demo_company: false
      // user_limit: null
    }
  });

  const onSubmit = (values) => {
    setCompanyInfo(values);

    handleNext();
  };

  useEffect(() => {
    if (companyInfo) {
      setValue('company_name', companyInfo?.company_name);
      setValue('web_url', companyInfo?.web_url);
      setValue('unique_url_component', companyInfo?.unique_url_component);
      setValue('support_email', companyInfo?.support_email);
      // setValue('subscription_plans', companyInfo?.subscription_plans);
      // setValue('update_frequency', companyInfo.update_frequency);
      setValue('group_of_companies', companyInfo.group_of_companies);
      setValue('fiscal_start_month', companyInfo.fiscal_start_month);
      // demo company values
      // setValue('is_demo_company', companyInfo.is_demo_company);
      // setValue('tenant_scale', companyInfo.tenant_scale || 'large');
      // setValue('user_limit', companyInfo.user_limit || null);
    }
  }, [companyInfo]);

  const watchFiscalYear = watch('fiscal_start_month');
  // const watchDemoCompany = watch('is_demo_company');

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="company_name">Company Name</InputLabel>
            <Controller
              name="company_name"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <OutlinedInput
                  id="company_name"
                  autoComplete={'on'}
                  type="text"
                  value={field.value}
                  name={field.name}
                  onBlur={() => {
                    field.onBlur();
                    setValue('unique_url_component', field?.value?.split(' ')?.[0]?.toLowerCase());
                  }}
                  onChange={field.onChange}
                  placeholder="Company Name"
                  fullWidth
                  error={Boolean(errors.company_name)}
                />
              )}
            />
            {errors.company_name?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-firstName"
              >
                {errors.company_name?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="web_url">Company Website</InputLabel>
            <Controller
              name="web_url"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <OutlinedInput
                  id="web_url"
                  type="text"
                  autoComplete={'on'}
                  value={field.value}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  placeholder="Company Website"
                  fullWidth
                  error={Boolean(errors.web_url)}
                />
              )}
            />
            {errors.web_url?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-web_url"
              >
                {errors.web_url?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="unique_url_component">Unique URL</InputLabel>
            <Controller
              name="unique_url_component"
              control={control}
              defaultValue=""
              sx={{ padding: '0px auto' }}
              render={({ field }) => (
                <OutlinedInput
                  sx={{ padding: '0px 5px' }}
                  startAdornment={
                    <InputAdornment
                      sx={{ background: theme.palette.slate[200], height: '28px', width: '95px', borderRadius: '6px', padding: '2px 10px' }}
                      position="start"
                    >
                      https://
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment
                      sx={{ background: theme.palette.slate[200], height: '28px', borderRadius: '6px', padding: '2px 10px' }}
                      position="end"
                    >
                      {COMPANY_URL_SUFFIX}
                    </InputAdornment>
                  }
                  id="unique_url_component"
                  type="text"
                  value={field.value}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={(e) => {
                    const value = e.target.value?.toLowerCase();
                    field.onChange(value);
                  }}
                  autoComplete={'on'}
                  placeholder="Your URL e.g. biosero"
                  fullWidth
                  error={Boolean(errors.unique_url_component)}
                />
              )}
            />
            {errors.unique_url_component?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-unique_url_component"
              >
                {errors.unique_url_component?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="email-login">Company Support Email</InputLabel>
            <Controller
              name="support_email"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <OutlinedInput
                  id="support_email"
                  type="email"
                  value={field?.value}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  placeholder="Enter Email"
                  autoComplete={'on'}
                  fullWidth
                  error={Boolean(errors.support_email)}
                />
              )}
            />
            {errors.support_email?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-support_email-login"
              >
                {errors.support_email?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="fiscal_start_month">Company Fiscal Year Period</InputLabel>
            <Controller
              //   name="state"
              name={'fiscal_start_month'}
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Select
                  displayEmpty
                  id="fiscal_start_month"
                  value={field?.value ?? ''}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 250 // Set the desired max height for the dropdown
                      }
                    }
                  }}
                  fullWidth
                  error={Boolean(errors?.fiscal_start_month)}
                  // renderValue={(selected) => {
                  //   if (selected?.length === 0) {
                  //     return <em>State / Province</em>;
                  //   }
                  //   return selected;
                  // }}
                >
                  <MenuItem
                    disabled
                    value=""
                  >
                    Select Fiscal Year Period
                  </MenuItem>
                  {Object.keys(fiscalYearValue)?.map((key) => (
                    <MenuItem value={key}>{fiscalYearValue[key].period}</MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors?.fiscal_start_month?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-fiscal_start_month"
              >
                {errors?.fiscal_start_month?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          mt={1}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="group_of_companies">Select Group Companies</InputLabel>
            <Controller
              name="group_of_companies"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  id="group_of_companies"
                  options={groupCompanies?.tenant_groups || []}
                  getOptionLabel={(option) => option.company_name || ''}
                  disableCloseOnSelect
                  value={field.value || []}
                  name={field.name}
                  isOptionEqualToValue={(option, value) => (option.id || option.tenant_id) === (value.id || value.tenant_id)}
                  // filterOptions={(options) => options}
                  onChange={(_, newValue) => {
                    setValue('group_of_companies', newValue, { shouldValidate: true });
                  }}
                  onBlur={field.onBlur}
                  sx={autocompleteStyle}
                  renderOption={(props, option, { selected }) => (
                    <Stack
                      direction={'row'}
                      spacing={1}
                      {...props}
                    >
                      <Box>
                        <Typography variant="body1">{option.company_name}</Typography>
                        {/* {option.support_email && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            {option.support_email}
                          </Typography>
                        )} */}
                      </Box>
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select group companies"
                      error={Boolean(errors.group_of_companies)}
                    />
                  )}
                  // renderTags={(selected, getTagProps) =>
                  //   selected.map((option, index) => (
                  //     <Chip
                  //       key={option.id || option.tenant_id}
                  //       {...getTagProps({ index })}
                  //       label={option.company_name}
                  //       onDelete={() => {
                  //         const newValue = selected.filter((item) => (item.id || item.tenant_id) !== (option.id || option.tenant_id));
                  //         setValue('group_of_companies', newValue, { shouldValidate: true });
                  //       }}
                  //     />
                  //   ))
                  // }
                />
              )}
            />
            {errors.group_of_companies?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-group_of_companies"
              >
                {errors.group_of_companies?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            sx={{ mt: '32px' }}
            spacing={1}
          >
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={() => navigate(-1)}
              sx={{ width: '200px', height: '40px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              // onClick={handleNext}
              sx={{ width: '200px', height: '40px' }}
            >
              Next
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
};

export default CompanyInfo;
